import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Wordmark } from "@/components/Logo";
import CheckoutButton from "@/components/CheckoutButton";
import { scrollToId, isOfferPath } from "@/lib/scroll";

const LINKS = [
  { id: "assessment", label: "Offers" },
  { id: "system", label: "System" },
  { id: "packages", label: "Packages" },
  { id: "products", label: "Products" },
  { id: "consulting", label: "Consulting" },
  { id: "guarantee", label: "Guarantee" },
  { id: "faq", label: "FAQ" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    if (!isOfferPath(location.pathname)) {
      navigate(`/#${id}`);
      return;
    }
    scrollToId(id);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? "bg-obsidian/85 backdrop-blur-xl border-b border-gold/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <button
          data-testid="nav-home-button"
          onClick={() => {
            setOpen(false);
            navigate("/");
          }}
          className="shrink-0"
        >
          <Wordmark />
        </button>

        <div className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => goTo(l.id)}
              className="text-sm text-slate-300 hover:text-gold transition-colors"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="hidden lg:block">
          <CheckoutButton
            lookup="aih_full_assessment"
            label="Assessment — $99.99"
            testid="nav-cta-assessment"
            className="rounded-full bg-gold text-obsidian font-semibold px-5 py-2.5 text-sm hover:bg-gold-bright transition-colors"
          />
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden p-2 text-slate-200 hover:text-gold transition-colors"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-obsidian/95 backdrop-blur-xl border-b border-gold/10"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => goTo(l.id)}
                  className="text-left py-3 text-slate-200 hover:text-gold border-b border-slate-800/60 transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <div className="mt-4">
                <CheckoutButton
                  lookup="aih_full_assessment"
                  label="Assessment — $99.99"
                  testid="nav-mobile-cta"
                  className="w-full rounded-full bg-gold text-obsidian font-semibold px-5 py-3 text-sm hover:bg-gold-bright transition-colors"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
